import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../../Navbar";
import { formatSalesValue } from "../../../utils/formatUtils";
import Footer from "../../Footer";

const CentralKitchenOpening = () => {
    const today = new Date().toLocaleDateString('en-CA');
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const inputRefs = useRef([]);

    const navigate = useNavigate();
    const location = useLocation();
    const {
        inventoryData,
        selectedOutlet,
        accessToken
    } = location.state || {};

    const [editableData, setEditableData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [startDate, setStartDate] = useState(today);

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/central-kitchen/inventory-calculator');
        }
    }, [accessToken, navigate]);

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        }
    };

    useEffect(() => {
        if (inventoryData) {
            const formattedData = Object.entries(inventoryData).map(
                ([name, item]) => ({
                    ...item,
                    name,
                    category: item.type,
                    groupName: item.group_name,
                    _key: `${item.type}-${item.group_name}-${name}`,
                    editableOpening: item.opening || 0,
                    primaryQty: "",
                    secondaryQty: ""
                })
            );

            setEditableData(formattedData);
        }
    }, [inventoryData]);

    const handlePrimaryChange = (itemKey, value) => {
        setEditableData(prev =>
            prev.map(item =>
                item._key === itemKey
                    ? {
                        ...item,
                        primaryQty: value,
                        editableOpening: computeFinalQuantity({
                            ...item,
                            primaryQty: value
                        })
                    }
                    : item
            )
        );
    };

    const handleSecondaryChange = (itemKey, value) => {
        setEditableData(prev =>
            prev.map(item =>
                item._key === itemKey
                    ? {
                        ...item,
                        secondaryQty: value,
                        editableOpening: computeFinalQuantity({
                            ...item,
                            secondaryQty: value
                        })
                    }
                    : item
            )
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();

            const currentIndex = inputRefs.current.indexOf(e.target);
            const nextInput = inputRefs.current[currentIndex + 1];

            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    function computeFinalQuantity(item) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition?.unit) || 1;
        const baseUom = (item.unitdefinition?.uom || '').toLowerCase();
        const itemUom = (item.uom || '').toLowerCase();
        const newUom = (item.new_uom || '').toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = (primaryInBaseUnit + secondaryInBaseUnit) / unitSize;

        // const totalInBaseUnit = primaryInBaseUnit + secondaryInBaseUnit;

        // if (itemUom !== baseUom) {
        //     return (totalInBaseUnit / unitSize);
        // }

        return totalInBaseUnit;
    }

    const shouldShowSecondary = (item) => {
        const itemUom = (item.uom || '').toLowerCase().trim();
        const newUom = (item.new_uom || '').toLowerCase().trim();
        const defUom = (item.unitdefinition?.uom || '').toLowerCase().trim();

        if (!defUom) {
            return itemUom !== newUom;
        }

        return !(itemUom === newUom && newUom === defUom);
    };

    const handleSave = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!editableData.length) {
                                toast.error("No items to save for selected category");
                                return;
                            }

                            setIsSaving(true);
                            try {
                                const selected = new Date(startDate);
                                selected.setDate(selected.getDate() - 1);

                                const previousDate = selected.toLocaleDateString('en-CA');

                                const payload = {
                                    token: localStorage.getItem('token'),
                                    outlet: selectedOutlet,
                                    start_date: previousDate,
                                    end_date: previousDate,
                                    physical_added: false,
                                    is_opening: true,
                                    items: editableData.map(item => ({
                                        name: item.name,
                                        type: item.category,
                                        group_name: item.groupName,
                                        uom: item.uom,
                                        new_uom: item.new_uom,
                                        opening_count: 0,
                                        total_received: 0,
                                        total_consumed: 0,
                                        closing_balance: parseFloat(item.editableOpening) || 0,
                                        physical_count: parseFloat(item.editableOpening) || 0
                                    }))
                                };

                                await axios.post(`${baseUrl}/save-inventory-snapshot-production`, payload);
                                toast.success('Opening counts saved successfully!');
                                navigate('/central-kitchen/inventory-calculator');
                            } catch (error) {
                                console.error('Error saving opening counts:', error);
                                toast.error(error?.response?.data?.error || 'Failed to save opening counts. Please try again.');
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-opening-count'
            }
        );
    };

    const computeCost = (item) => {
        const qty = computeFinalQuantity(item);
        const rate = parseFloat(item.opening_rate) || 0;

        return qty * rate;
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');

        const filtered = editableData
            .sort((a, b) => {
                const nameA = (a.name || "").toLowerCase();
                const nameB = (b.name || "").toLowerCase();

                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });

        const content = `
        <html>
        <head>
            <title>Stock Count Sheet</title>
            <style>
               body {
                    font-family: Arial, sans-serif;
                    padding: 5px;
                }
                .header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 15px;
                    font-size: 14px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }
                th, td {
                    border: 1px solid black;
                    padding: 8px;
                    text-align: center;
                }
                th {
                    background-color: #f2f2f2;
                }
                .qty-box {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                }
                .line {
                    width: 60px;
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div>Stock Count for Central Kitchen</div>
                <div>Date: ____________</div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>Item Name</th>
                        <th>Unit Definition</th>
                        <th>Primary</th>
                        <th>Secondary</th>
                    </tr>
                </thead>
                <tbody>
                    ${filtered.map(item => {
            const showSecondary = item.uom !== item.new_uom;

            return `
                            <tr>
                                <td>${item.name}</td>
                                <td>${item.unitdefinition?.unit || ''} (${item.unitdefinition?.uom || ''})</td>

                                <td>
                                    <div class="qty-box">
                                        <span class="line"></span>
                                        <span>${item.uom}</span>
                                    </div>
                                </td>

                                <td>
                                ${showSecondary
                    ? `
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.new_uom}</span>
                                        </div>
                                        `
                    : '-'
                }
                                </td>
                            </tr>
                        `;
        }).join('')}
                </tbody>
            </table>

        </body>
        </html>
    `;

        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="mx-auto max-w-6xl p-4">
                    <h3 className="font-semibold text-xl">
                        <span className="text-gray-600">Opening Count for</span> {selectedOutlet}
                    </h3>

                    <div className="flex justify-between items-center my-3">
                        <input
                            type="date"
                            className="border p-1 rounded"
                            value={startDate}
                            max={today}
                            onChange={(e) => handleDateChange(e, 'start')}
                        />

                        <button
                            onClick={handlePrint}
                            className="px-3 py-2 rounded-sm text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                        >
                            Print Sheet
                        </button>
                    </div>

                    <div className="mt-3 rounded-md border overflow-auto max-h-[650px] tiny-scrollbar">
                        <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                            <thead className="bg-gray-50 sticky top-0 z-20">
                                <tr>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rate
                                    </th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cost
                                    </th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {(() => {
                                    inputRefs.current = [];

                                    return editableData.length > 0 ? (
                                        editableData
                                            .sort((a, b) =>
                                                a.name.localeCompare(b.name)
                                            )
                                            .map((item, index) => {
                                                const showSecondary = shouldShowSecondary(item);

                                                return (
                                                    <tr key={`${item.category}-${item.groupName}-${item.name}-${index}`} className="hover:bg-gray-100">
                                                        <td className="px-3 py-2">{item.name}</td>
                                                        <td className="px-3 py-2">{item.unitdefinition?.unit || 1} ({item.unitdefinition?.uom || item.uom})</td>
                                                        <td className="px-3 py-2">
                                                            {item.opening_rate ? formatSalesValue(item.opening_rate) : "-"}
                                                        </td>

                                                        <td className="px-3 py-2 border-r">{item.uom}</td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                ref={(el) => {
                                                                    if (el) inputRefs.current.push(el);
                                                                }}
                                                                type="number"
                                                                min="0"
                                                                step="any"
                                                                value={item.primaryQty}
                                                                className="w-20 px-2 py-1 border rounded text-center"
                                                                onChange={(e) => handlePrimaryChange(item._key, e.target.value)}
                                                                onKeyDown={(e) => handleKeyDown(e)}
                                                            />
                                                        </td>

                                                        <td className="px-3 py-2 border-r">
                                                            {showSecondary ? item.unitdefinition?.uom || item.new_uom : '-'}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {showSecondary ? (
                                                                <input
                                                                    ref={(el) => {
                                                                        if (el) inputRefs.current.push(el);
                                                                    }}
                                                                    type="number"
                                                                    min="0"
                                                                    step="any"
                                                                    value={item.secondaryQty}
                                                                    className="w-20 px-2 py-1 border rounded text-center"
                                                                    onChange={(e) => handleSecondaryChange(item._key, e.target.value)}
                                                                    onKeyDown={(e) => handleKeyDown(e)}
                                                                />
                                                            ) : '-'}
                                                        </td>
                                                        <td className="px-3 py-2">{formatSalesValue(computeCost(item))}</td>
                                                    </tr>
                                                );
                                            })
                                    ) : (
                                        <tr>
                                            <td colSpan={8} className="px-3 py-4 text-gray-500">
                                                No data available
                                            </td>
                                        </tr>
                                    )
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {editableData.length > 0 && (
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                            >
                                {isSaving ? 'Saving...' : 'Save Opening Counts'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default CentralKitchenOpening;